import React, { useState, useRef, useEffect } from 'react';
import api from '../api';
import { useNumbers } from '../context/NumberContext';

const EMPTY_BIZ  = { label: '', business_account_id: '', access_token: '', webhook_verify_token: '' };
const EMPTY_NUM  = { label: '', phone_number_id: '', display_phone: '' };

export default function Businesses() {
  const { businesses, activeBusiness, selectBusiness, numbers,
          activeNumber, selectNumber, fetchAll, fetchNumbers, loaded } = useNumbers();

  const [bizForm, setBizForm]       = useState(EMPTY_BIZ);
  const [webhookBizId, setWebhookBizId] = useState(null); // which biz's webhook panel is open
  const [editingBiz, setEditingBiz] = useState(null);
  const [showBizForm, setShowBizForm] = useState(false);
  const [showToken, setShowToken]   = useState(false);

  const [numForm, setNumForm]       = useState(EMPTY_NUM);
  const [editingNum, setEditingNum] = useState(null);
  const [showNumForm, setShowNumForm] = useState(false);

  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [msg, setMsg]               = useState('');
  const importRef                   = useRef();

  const flash = (m, err=false) => {
    err ? setError(m) : setMsg(m);
    setTimeout(() => { setError(''); setMsg(''); }, 4000);
  };

  // ── Business CRUD ──────────────────────────────────────
  const openAddBiz = () => {
    setEditingBiz(null);
    setBizForm({ ...EMPTY_BIZ, webhook_verify_token: 'verify'+Math.random().toString(36).slice(2,8) });
    setShowToken(false); setError('');
    setShowBizForm(true);
  };

  const openEditBiz = async (b) => {
    try {
      const res = await api.get(`/businesses/${b.id}/full`);
      setEditingBiz(res.data);
      setBizForm({ label: res.data.label, business_account_id: res.data.business_account_id,
        access_token: res.data.access_token, webhook_verify_token: res.data.webhook_verify_token });
      setShowToken(false); setError('');
      setShowBizForm(true);
    } catch (e) { flash(e.response?.data?.error || 'Load failed', true); }
  };

  const saveBiz = async () => {
    if (!bizForm.label || !bizForm.business_account_id || !bizForm.access_token)
      return setError('Label, Business Account ID, and Access Token are required.');
    setSaving(true);
    try {
      if (editingBiz) {
        await api.put(`/businesses/${editingBiz.id}`, bizForm);
        flash('Business updated!');
      } else {
        await api.post('/businesses', bizForm);
        flash('Business added!');
      }
      setShowBizForm(false); setEditingBiz(null);
      await fetchAll();
    } catch (e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const deleteBiz = async (b) => {
    if (!window.confirm(`Delete "${b.label}"?\n\nAll numbers, messages, contacts and flows under this business will be deleted.`)) return;
    try {
      await api.delete(`/businesses/${b.id}`);
      if (activeBusiness?.id === b.id) selectBusiness(null);
      flash('Business deleted.');
      await fetchAll();
    } catch (e) { flash(e.response?.data?.error || 'Delete failed', true); }
  };

  // ── Number CRUD ────────────────────────────────────────
  const openAddNum = () => {
    setEditingNum(null); setNumForm(EMPTY_NUM); setError('');
    setShowNumForm(true);
  };

  const openEditNum = (n) => {
    setEditingNum(n);
    setNumForm({ label: n.label, phone_number_id: n.phone_number_id, display_phone: n.display_phone||'' });
    setError(''); setShowNumForm(true);
  };

  const saveNum = async () => {
    if (!numForm.label || !numForm.phone_number_id)
      return setError('Label and Phone Number ID are required.');
    if (!activeBusiness) return setError('Select a business first.');
    setSaving(true);
    try {
      if (editingNum) {
        await api.put(`/businesses/${activeBusiness.id}/numbers/${editingNum.id}`, numForm);
        flash('Number updated!');
      } else {
        await api.post(`/businesses/${activeBusiness.id}/numbers`, numForm);
        flash('Number added!');
      }
      setShowNumForm(false); setEditingNum(null);
      await fetchNumbers();
    } catch (e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const deleteNum = async (n) => {
    if (!window.confirm(`Delete number "${n.label}"?\n\nAll messages and contacts for this number will be deleted.`)) return;
    try {
      await api.delete(`/businesses/${activeBusiness.id}/numbers/${n.id}`);
      if (activeNumber?.id === n.id) selectNumber(null);
      flash('Number deleted.');
      await fetchNumbers();
    } catch (e) { flash(e.response?.data?.error || 'Delete failed', true); }
  };

  // ── Import / Export ────────────────────────────────────
  const exportCreds = async () => {
    try {
      const res = await api.get('/businesses/export');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href=url; a.download='wa_credentials.json'; a.click();
      URL.revokeObjectURL(url);
      flash('Credentials exported!');
    } catch { flash('Export failed', true); }
  };

  const importCreds = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      // Support both old format (numbers[]) and new format (businesses[])
      let payload;
      if (data.businesses) {
        payload = { businesses: data.businesses };
      } else if (data.numbers) {
        // Legacy: single business inferred from first number's business_account_id
        const biz = data.numbers[0];
        payload = { businesses: [{ label: 'Imported Business',
          business_account_id: biz.business_account_id, access_token: biz.access_token,
          webhook_verify_token: biz.webhook_verify_token||'verify123',
          numbers: data.numbers.map(n => ({ label: n.label||n.phone_number_id,
            phone_number_id: n.phone_number_id, display_phone: n.display_phone||'' })) }] };
      } else { flash('Invalid file format', true); e.target.value=''; return; }

      const res = await api.post('/businesses/import', payload);
      flash(`✅ Imported ${res.data.imported_businesses} business(es), ${res.data.imported_numbers} number(s)!`);
      await fetchAll();
    } catch (ex) { flash(ex.response?.data?.error || ex.message || 'Import failed', true); }
    e.target.value = '';
  };

  const webhookUrl = `${window.location.origin}/api/webhook`;

  return (
    <div style={{ padding: '20px 16px', maxWidth: 820, margin: '0 auto' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ fontFamily: 'var(--mono)', fontSize: 13, letterSpacing: '0.06em', color: 'var(--text2)', margin: 0 }}>
          🏢 BUSINESSES & NUMBERS
        </h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => importRef.current?.click()} className="btn-ghost" style={{ fontSize: 12 }}>📥 Import JSON</button>
          <input ref={importRef} type="file" accept=".json" style={{ display: 'none' }} onChange={importCreds} />
          {businesses.length > 0 && (
            <button onClick={exportCreds} className="btn-ghost" style={{ fontSize: 12 }}>📤 Export JSON</button>
          )}
          <button onClick={openAddBiz} className="btn-primary" style={{ fontSize: 13 }}>+ Add Business</button>
        </div>
      </div>

      {msg   && <div style={fStyle('ok')}>{msg}</div>}
      {error && !showBizForm && !showNumForm && <div style={fStyle('err')}>{error}</div>}

      {/* Webhook info */}
      <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '10px 14px', marginBottom: 18, fontSize: 12 }}>
        <span style={{ color: 'var(--text2)', fontWeight: 600 }}>Webhook URL: </span>
        <code style={{ color: 'var(--accent)', wordBreak: 'break-all' }}>{webhookUrl}</code>
        <button onClick={() => { navigator.clipboard.writeText(webhookUrl); flash('Copied!'); }}
          className="btn-ghost" style={{ fontSize: 11, marginLeft: 8, padding: '2px 8px' }}>Copy</button>
        <div style={{ color: 'var(--text3)', marginTop: 4, fontSize: 11 }}>
          Set this in Meta Developer Console → WhatsApp → Configuration. Each business has its own Verify Token.
        </div>
      </div>

      {/* ── Empty state ── */}
      {loaded && businesses.length === 0 && !showBizForm && (
        <div style={{ textAlign: 'center', padding: '50px 20px' }}>
          <div style={{ fontSize: 48, marginBottom: 14 }}>🏢</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No Business Added Yet</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 22, lineHeight: 1.7 }}>
            Add a WhatsApp Business Account to get started.<br />
            Each business can have multiple phone numbers.
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button onClick={openAddBiz} className="btn-primary">+ Add Business</button>
            <button onClick={() => importRef.current?.click()} className="btn-ghost">📥 Import JSON</button>
          </div>
        </div>
      )}

      {/* ── Business list ── */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: showBizForm ? 16 : 0 }}>
        {businesses.map(b => {
          const isActive = activeBusiness?.id === b.id;
          return (
            <div key={b.id} className="card"
              onClick={() => selectBusiness(b)}
              style={{ flex: '1 1 340px', cursor: 'pointer', transition: 'border-color 0.15s',
                border: `1px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`,
                background: isActive ? 'rgba(37,211,102,0.04)' : undefined }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    {isActive && <span style={{ background: 'var(--accent)', color: '#000', fontSize: 10,
                      fontWeight: 700, borderRadius: 4, padding: '2px 7px', fontFamily: 'var(--mono)' }}>ACTIVE</span>}
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{b.label}</span>
                    {b.username && <span style={{ fontSize: 11, color: 'var(--text3)' }}>({b.username})</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                    WABA: <code style={{ color: 'var(--text2)', fontSize: 11 }}>{b.business_account_id}</code>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                    Verify Token: <code style={{ color: 'var(--text2)', fontSize: 11 }}>{b.webhook_verify_token}</code>
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 11, color: 'var(--text3)' }}>
                    <span>📱 {b.number_count||0} number{b.number_count!==1?'s':''}</span>
                    <span>📋 {b.template_count||0} templates</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => setWebhookBizId(b.id)} className="btn-ghost"
                    title="Webhook Forward & Health" style={{ fontSize: 12 }}>🔗</button>
                  <button onClick={() => openEditBiz(b)} className="btn-ghost" style={{ fontSize: 12 }}>✏️</button>
                  <button onClick={() => deleteBiz(b)} className="btn-ghost"
                    style={{ fontSize: 12, color: 'var(--danger)' }}>🗑</button>
                </div>
              </div>

              {/* Numbers under this business (only show if active) */}
              {isActive && (
                <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', letterSpacing: '0.04em' }}>PHONE NUMBERS</span>
                    <button onClick={(e) => { e.stopPropagation(); openAddNum(); }} className="btn-ghost"
                      style={{ fontSize: 11, padding: '3px 10px' }}>+ Add Number</button>
                  </div>
                  {numbers.length === 0 && (
                    <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', padding: '10px 0' }}>
                      No numbers yet — add one to start messaging
                    </div>
                  )}
                  {numbers.map(n => (
                    <div key={n.id}
                      onClick={e => { e.stopPropagation(); selectNumber(n); }}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '7px 10px', borderRadius: 'var(--r-sm)', marginBottom: 4, cursor: 'pointer',
                        background: activeNumber?.id === n.id ? 'var(--accent-dim)' : 'var(--bg3)',
                        border: `1px solid ${activeNumber?.id === n.id ? 'var(--accent)' : 'var(--border)'}`,
                        transition: 'all 0.15s' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {activeNumber?.id === n.id && (
                            <span style={{ background: 'var(--accent)', color: '#000', fontSize: 9,
                              fontWeight: 700, borderRadius: 3, padding: '1px 5px', fontFamily: 'var(--mono)' }}>●</span>
                          )}
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{n.label}</span>
                        </div>
                        {n.display_phone && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{n.display_phone}</div>}
                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>ID: {n.phone_number_id}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                        <button onClick={() => openEditNum(n)} className="btn-ghost" style={{ fontSize: 11, padding: '3px 8px' }}>✏️</button>
                        <button onClick={() => deleteNum(n)} className="btn-ghost"
                          style={{ fontSize: 11, padding: '3px 8px', color: 'var(--danger)' }}>🗑</button>
                      </div>
                    </div>
                  ))}
                  {showNumForm && isActive && (
                    <div style={{ marginTop: 10, padding: 14, background: 'var(--bg2)', borderRadius: 'var(--r-sm)',
                      border: '1px solid var(--accent)' }}>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--accent)', marginBottom: 12 }}>
                        {editingNum ? '// EDIT NUMBER' : '// ADD NUMBER'}
                      </div>
                      {error && <div style={{ ...fStyle('err'), marginBottom: 10 }}>{error}</div>}
                      {[
                        { k: 'label', l: 'Label *', ph: 'e.g. Sales, Support' },
                        { k: 'phone_number_id', l: 'Phone Number ID *', ph: '639242735936092', disabled: !!editingNum },
                        { k: 'display_phone', l: 'Display Phone', ph: '+91 98765 43210' },
                      ].map(f => (
                        <div key={f.k} style={{ marginBottom: 10 }}>
                          <label className="label" style={{ fontSize: 11 }}>{f.l}</label>
                          <input value={numForm[f.k]} disabled={f.disabled}
                            style={{ fontSize: 13, opacity: f.disabled ? 0.6 : 1 }}
                            onChange={e => setNumForm(p => ({ ...p, [f.k]: e.target.value }))}
                            placeholder={f.ph} />
                        </div>
                      ))}
                      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        <button onClick={saveNum} className="btn-primary" disabled={saving} style={{ fontSize: 12 }}>
                          {saving ? 'Saving…' : editingNum ? '💾 Update' : '➕ Add'}
                        </button>
                        <button onClick={() => { setShowNumForm(false); setEditingNum(null); setError(''); }}
                          className="btn-ghost" style={{ fontSize: 12 }}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Business add/edit form ── */}
      {showBizForm && (
        <div className="card" style={{ border: '1px solid var(--accent)', marginTop: 8 }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)', marginBottom: 16 }}>
            {editingBiz ? '// EDIT BUSINESS' : '// ADD BUSINESS (WABA)'}
          </div>
          {error && <div style={{ ...fStyle('err'), marginBottom: 14 }}>{error}</div>}

          <F label="Label *"><input value={bizForm.label} placeholder="e.g. Acme Corp India"
            onChange={e => setBizForm(p => ({...p, label: e.target.value}))} autoFocus /></F>

          <F label="Business Account ID (WABA ID) *">
            <input value={bizForm.business_account_id} placeholder="101030342797199"
              disabled={!!editingBiz} style={{ opacity: editingBiz ? 0.6 : 1 }}
              onChange={e => setBizForm(p => ({...p, business_account_id: e.target.value}))} />
            {editingBiz && <Hint>WABA ID cannot be changed</Hint>}
            {!editingBiz && <Hint>Found in Meta Business Manager → WhatsApp Accounts</Hint>}
          </F>

          <F label="System User Access Token *">
            <div style={{ position: 'relative' }}>
              <input type={showToken ? 'text' : 'password'} value={bizForm.access_token}
                placeholder="EAAxxxxx…" style={{ paddingRight: 80 }}
                onChange={e => setBizForm(p => ({...p, access_token: e.target.value}))} />
              <button type="button" onClick={() => setShowToken(s => !s)} className="btn-ghost"
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 11, padding: '3px 8px' }}>
                {showToken ? '🙈 Hide' : '👁 Show'}
              </button>
            </div>
            <Hint>Meta Business Manager → System Users → Generate Token (permanent, not temporary)</Hint>
          </F>

          <F label="Webhook Verify Token">
            <input value={bizForm.webhook_verify_token} placeholder="anyrandomstring"
              onChange={e => setBizForm(p => ({...p, webhook_verify_token: e.target.value}))} />
            <Hint>Use this exact value in Meta Developer Console when setting up the webhook</Hint>
          </F>

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button onClick={saveBiz} className="btn-primary" disabled={saving} style={{ minWidth: 140 }}>
              {saving ? 'Saving…' : editingBiz ? '💾 Update Business' : '🏢 Add Business'}
            </button>
            <button type="button" onClick={() => { setShowBizForm(false); setEditingBiz(null); setError(''); }}
              className="btn-ghost">Cancel</button>
          </div>
        </div>
      )}

      {/* Webhook Manager Modal */}
      {webhookBizId && (
        <WebhookManager
          bizId={webhookBizId}
          businesses={businesses}
          onClose={() => setWebhookBizId(null)}
          flashOk={flashOk}
          flashErr={flashErr}
        />
      )}

      {/* Setup guide */}
      <div style={{ marginTop: 24, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '14px 16px', fontSize: 12, color: 'var(--text3)', lineHeight: 2 }}>
        <div style={{ color: 'var(--text2)', fontWeight: 600, marginBottom: 6 }}>📋 How it works</div>
        <div>1. <strong>Add a Business</strong> — one entry per WhatsApp Business Account (WABA)</div>
        <div>2. <strong>Add Numbers</strong> — one or more phone numbers under each business</div>
        <div>3. <strong>Select Business + Number</strong> in sidebar to switch context</div>
        <div>4. <strong>Templates</strong> are shared by all numbers under the same business</div>
        <div>5. Webhook URL above → set in Meta Dev Console with the business's Verify Token</div>
        <div>6. <strong>Webhook Forward</strong> — click 🔗 on any business to forward messages to your API/n8n/Zapier</div>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label className="label" style={{ display: 'block', marginBottom: 5, fontSize: 12, color: 'var(--text2)' }}>{label}</label>
      {children}
    </div>
  );
}
function Hint({ children }) {
  return <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, lineHeight: 1.5 }}>{children}</div>;
}
function fStyle(t) {
  return { padding: '10px 14px', borderRadius: 'var(--r-sm)', fontSize: 13,
    background: t==='ok' ? 'rgba(79,209,197,0.08)' : 'rgba(239,68,68,0.08)',
    border: `1px solid ${t==='ok' ? 'var(--accent)' : 'var(--danger)'}`,
    color: t==='ok' ? 'var(--accent)' : 'var(--danger)' };
}

// ── WebhookManager Component ──────────────────────────────
function WebhookManager({ bizId, businesses, onClose, flashOk, flashErr }) {
  const biz = businesses.find(b => b.id === bizId);
  const [cfg, setCfg]         = useState({ forward_url: '', forward_url_2: '', forward_secret: '', forward_enabled: false });
  const [health, setHealth]   = useState(null);  // { subscribed, apps, checked_at }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [testing, setTesting] = useState('');     // which url is being tested
  const [testResult, setTestResult] = useState({});
  const [healthLoading, setHealthLoading] = useState(false);

  useEffect(() => {
    api.get(`/businesses/${bizId}/webhook-forward`)
      .then(r => { setCfg(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [bizId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/businesses/${bizId}/webhook-forward`, cfg);
      flashOk('✅ Webhook forward settings saved!');
    } catch (e) { flashErr(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const testUrl = async (urlKey) => {
    const url = cfg[urlKey];
    if (!url) return;
    setTesting(urlKey);
    setTestResult(p => ({ ...p, [urlKey]: null }));
    try {
      const r = await api.post(`/businesses/${bizId}/webhook-forward/test`, { url });
      setTestResult(p => ({ ...p, [urlKey]: r.data }));
    } catch (e) {
      setTestResult(p => ({ ...p, [urlKey]: { success: false, error: e.response?.data?.error || e.message } }));
    }
    setTesting('');
  };

  const checkHealth = async () => {
    setHealthLoading(true);
    setHealth(null);
    try {
      const r = await api.get(`/businesses/${bizId}/webhook-health`);
      setHealth(r.data);
    } catch (e) {
      setHealth({ error: e.response?.data?.error || e.message });
    }
    setHealthLoading(false);
  };

  const fixSubscription = async () => {
    setHealthLoading(true);
    try {
      await api.post(`/businesses/${bizId}/webhook-subscribe`);
      flashOk('✅ WABA subscribed to app! Webhooks are now active.');
      await checkHealth();
    } catch (e) {
      flashErr(e.response?.data?.error || 'Subscribe failed — check your token permissions');
    }
    setHealthLoading(false);
  };

  const genSecret = () => {
    const s = 'whsec_' + Array.from(crypto.getRandomValues(new Uint8Array(24)))
      .map(b => b.toString(16).padStart(2, '0')).join('');
    setCfg(p => ({ ...p, forward_secret: s }));
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={onClose}>
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 16,
        width: '100%', maxWidth: 620, maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          position: 'sticky', top: 0, background: 'var(--bg2)', zIndex: 1 }}>
          <div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)', letterSpacing: '0.06em' }}>
              🔗 WEBHOOK MANAGER
            </div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>{biz?.label}</div>
          </div>
          <button onClick={onClose} style={{ background: 'var(--bg3)', border: '1px solid var(--border)',
            borderRadius: 8, width: 32, height: 32, cursor: 'pointer', color: 'var(--text)',
            fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Section 1: WABA Health Check */}
          <Section title="🏥 WABA Webhook Health" subtitle="Check if Meta is actually delivering webhooks to your app (critical — many miss this step)">
            {health?.error && <ErrBox msg={health.error} />}
            {health && !health.error && (
              <div style={{ padding: '12px 14px', borderRadius: 'var(--r-sm)', marginBottom: 12,
                background: health.subscribed ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                border: `1px solid ${health.subscribed ? '#10b981' : 'var(--danger)'}` }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: health.subscribed ? '#10b981' : 'var(--danger)', marginBottom: 4 }}>
                  {health.subscribed ? '✅ Subscribed — webhooks are active' : '❌ NOT Subscribed — messages will not be delivered!'}
                </div>
                {health.subscribed && health.apps?.length > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                    App: {health.apps[0].name || health.apps[0].id} · Checked: {new Date(health.checked_at).toLocaleTimeString()}
                  </div>
                )}
                {!health.subscribed && (
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                    Your WABA is not subscribed to your app. Click "Fix Now" to enable webhook delivery.
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={checkHealth} disabled={healthLoading} className="btn-ghost" style={{ fontSize: 12 }}>
                {healthLoading ? '⏳ Checking…' : '🔍 Check Status'}
              </button>
              {health && !health.subscribed && (
                <button onClick={fixSubscription} disabled={healthLoading} className="btn-primary" style={{ fontSize: 12 }}>
                  ⚡ Fix Now — Subscribe WABA
                </button>
              )}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8, lineHeight: 1.6 }}>
              💡 <strong>What this does:</strong> Calls <code>POST /{'{WABA_ID}'}/subscribed_apps</code> — required for Meta to actually
              deliver webhook events to your app. Without this, webhooks verify but messages never arrive.
            </div>
          </Section>

          {/* Section 2: Forward URLs */}
          <Section title="📡 Webhook Forward URLs"
            subtitle="Forward all incoming messages & events to your own API, n8n, Zapier, Make, or any webhook endpoint">

            {loading ? <div style={{ color: 'var(--text3)', fontSize: 12 }}>Loading…</div> : (
              <>
                {/* Enable toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
                  padding: '10px 14px', background: 'var(--bg3)', borderRadius: 'var(--r-sm)',
                  border: '1px solid var(--border)' }}>
                  <input type="checkbox" id="fw-enabled" checked={cfg.forward_enabled}
                    onChange={e => setCfg(p => ({ ...p, forward_enabled: e.target.checked }))}
                    style={{ width: 16, height: 16, cursor: 'pointer' }} />
                  <label htmlFor="fw-enabled" style={{ cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                    Enable webhook forwarding for this business
                  </label>
                </div>

                {/* Primary URL */}
                <UrlField
                  label="Primary Forward URL"
                  placeholder="https://your-n8n.com/webhook/abc123"
                  value={cfg.forward_url}
                  onChange={v => setCfg(p => ({ ...p, forward_url: v }))}
                  onTest={() => testUrl('forward_url')}
                  testing={testing === 'forward_url'}
                  result={testResult.forward_url}
                  hint="All inbound messages and delivery status events are POSTed here in real-time"
                />

                {/* Secondary URL */}
                <UrlField
                  label="Secondary Forward URL (optional)"
                  placeholder="https://hooks.zapier.com/hooks/catch/..."
                  value={cfg.forward_url_2}
                  onChange={v => setCfg(p => ({ ...p, forward_url_2: v }))}
                  onTest={() => testUrl('forward_url_2')}
                  testing={testing === 'forward_url_2'}
                  result={testResult.forward_url_2}
                  hint="Sent alongside primary URL — useful for dual integrations (e.g. n8n + analytics)"
                />

                {/* Secret */}
                <div style={{ marginBottom: 0 }}>
                  <label className="label" style={{ fontSize: 12 }}>Signing Secret (optional)</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type="text" value={cfg.forward_secret} placeholder="whsec_..."
                      onChange={e => setCfg(p => ({ ...p, forward_secret: e.target.value }))}
                      style={{ flex: 1, fontFamily: 'var(--mono)', fontSize: 12 }} />
                    <button onClick={genSecret} className="btn-ghost" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                      🔑 Generate
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, lineHeight: 1.6 }}>
                    If set, each request includes <code>X-WA-Signature: sha256=...</code> header.<br />
                    Verify: <code>HMAC(secret, timestamp + '.' + body) === signature</code>
                  </div>
                </div>

                <button onClick={save} disabled={saving} className="btn-primary" style={{ marginTop: 16, fontSize: 13 }}>
                  {saving ? 'Saving…' : '💾 Save Forward Settings'}
                </button>
              </>
            )}
          </Section>

          {/* Section 3: Payload format */}
          <Section title="📋 Payload Format" subtitle="What your endpoint receives on each event">
            <pre style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8,
              padding: '12px 14px', fontSize: 11, fontFamily: 'var(--mono)', overflow: 'auto',
              color: 'var(--text2)', lineHeight: 1.6, maxHeight: 200 }}>
{`{
  "_meta": {
    "source": "wa-dashboard",
    "event": "inbound",          // inbound | status | raw
    "wa_number_id": 1,
    "phone_number_id": "639242...",
    "number_label": "Main Number",
    "ts": 1718000000000          // unix ms
  },
  "metadata": { "phone_number_id": "...", "display_phone_number": "..." },
  "messages": [
    {
      "id": "wamid.xxx",
      "from": "919876543210",
      "timestamp": "1718000000",
      "type": "text",
      "text": { "body": "Hello!" }
    }
  ],
  "contacts": [
    { "profile": { "name": "John Doe" }, "wa_id": "919876543210" }
  ]
}`}
            </pre>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8, lineHeight: 1.7 }}>
              ℹ️ For <strong>status</strong> events, <code>messages</code> is replaced by <code>statuses</code> array.<br />
              Your endpoint must return HTTP 2xx within 8 seconds. Failed forwards are logged (not retried).
            </div>
          </Section>

        </div>
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)',
      borderRadius: 'var(--r-sm)', padding: '16px' }}>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)', marginBottom: 4 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12 }}>{subtitle}</div>}
      {children}
    </div>
  );
}

function UrlField({ label, placeholder, value, onChange, onTest, testing, result, hint }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label className="label" style={{ fontSize: 12 }}>{label}</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <input type="url" value={value} placeholder={placeholder}
          onChange={e => onChange(e.target.value)}
          style={{ flex: 1, fontSize: 12, fontFamily: 'var(--mono)' }} />
        <button onClick={onTest} disabled={testing || !value} className="btn-ghost"
          style={{ fontSize: 12, whiteSpace: 'nowrap', opacity: value ? 1 : 0.4 }}>
          {testing ? '⏳' : '🧪 Test'}
        </button>
      </div>
      {result && (
        <div style={{ marginTop: 6, fontSize: 11, padding: '6px 10px', borderRadius: 6,
          background: result.success ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
          border: `1px solid ${result.success ? '#10b981' : 'var(--danger)'}`,
          color: result.success ? '#10b981' : 'var(--danger)' }}>
          {result.success
            ? `✅ Success — HTTP ${result.status} in ${result.ms}ms`
            : `❌ Failed — ${result.error}${result.status ? ` (HTTP ${result.status})` : ''}`}
        </div>
      )}
      {hint && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function ErrBox({ msg }) {
  return (
    <div style={{ padding: '8px 12px', borderRadius: 'var(--r-sm)', marginBottom: 12, fontSize: 12,
      background: 'rgba(239,68,68,0.08)', border: '1px solid var(--danger)', color: 'var(--danger)' }}>
      ⚠️ {msg}
    </div>
  );
}
